import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import Reception from './components/reception.jsx';
import Taskbar from './components/Taskbar.jsx';
import EventsModal from './components/EventsModal.jsx';
import Login from './components/Login.jsx';
import AgreementModal from './components/AgreementModal';
import supabase from './supabaseClient';

const ABOUT_TEXT =
  'ganache was conceived 2003, when my wife was planning for our wedding. ' +
  'like many, she had been dreaming of her ideal wedding since she was young. ' +
  'however, both of us didn\'t know where to start, how much money to budget for, ' +
  'where we should splurge, where we should save. so like many we turned to wedding ' +
  'magazines, wedding fairs, talked to wedding planners, called various venues: ' +
  'vineyards, american and chinese restaurants, churches, golf courses. add that onto ' +
  'parents with their asks, wishes, and questions all of which we didn\'t have answers to. ' +
  'there\'s a reason planning the ideal wedding turns brides into bridezilla. but it doesn\'t ' +
  'have to be that way anymore. what if part of the wedding planning process could be simple, ' +
  'smooth and methodical? it can be, and it starts with ganache.';

const MIN_WIDTH = 834; // iPad Pro 11" portrait width

function App() {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [screenTooSmall, setScreenTooSmall] = useState(window.innerWidth < MIN_WIDTH);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [titleBottom, setTitleBottom] = useState(0);
  const [currentEventId, setCurrentEventId] = useState(() => {
    const saved = localStorage.getItem('currentEventId');
    return saved ? Number(saved) : 1;
  });
  const [events, setEvents] = useState([]);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [pendingGuestRole, setPendingGuestRole] = useState(null);
  const [pendingNewEventGuests, setPendingNewEventGuests] = useState([]);
  const [pendingVenueForEvent, setPendingVenueForEvent] = useState(false);
  const [eventVenueResult, setEventVenueResult] = useState(null);
  const [venues, setVenues] = useState([]);
  const [groomName, setGroomName] = useState(null);
  const [brideName, setBrideName] = useState(null);
  const [weddingUrl, setWeddingUrl] = useState(null);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [urlValidating, setUrlValidating] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutKey, setAboutKey] = useState(0);
  const [aboutDone, setAboutDone] = useState(false);
  const [aboutAtBottom, setAboutAtBottom] = useState(false);
  const aboutContainerRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calNavDate, setCalNavDate] = useState(new Date());
  const [needsAgreement, setNeedsAgreement] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => setSession(s ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) { setNeedsAgreement(null); return; }
    setNeedsAgreement(!session.user.user_metadata?.tos_agreed_at);
  }, [session]);

  useEffect(() => {
    const el = document.getElementById('ganache');
    if (el) {
      const update = () => {
        const rect = el.getBoundingClientRect();
        setTitleBottom(rect.bottom + window.scrollY);
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
  }, []);

  useEffect(() => {
    const checkSize = () => setScreenTooSmall(window.innerWidth < MIN_WIDTH);
    const checkOrientation = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', checkSize);
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkSize);
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Load events for the logged-in user; seed a default event if none exist
  useEffect(() => {
    if (!session?.user?.id) return;
    const userId = session.user.id;
    supabase.from('events').select('*').order('id', { ascending: true })
      .then(async ({ data, error }) => {
        if (error) { console.error('events load failed:', error.message); return; }
        if (data && data.length > 0) {
          setEvents(data);
          const savedId = Number(localStorage.getItem('currentEventId'));
          const valid = savedId && data.some(e => e.id === savedId);
          const id = valid ? savedId : data[0].id;
          setCurrentEventId(id);
          localStorage.setItem('currentEventId', id);
          const currentEvent = data.find(e => e.id === id);
          if (currentEvent?.groom_name) setGroomName(currentEvent.groom_name);
          if (currentEvent?.bride_name) setBrideName(currentEvent.bride_name);
        } else {
          const { data: evt, error: e } = await supabase
            .from('events')
            .insert({ date: null, groom_name: null, bride_name: null, venue: null, user_id: userId })
            .select().single();
          if (e) { console.error('default event create failed:', e.message); return; }
          setEvents([evt]);
          setCurrentEventId(evt.id);
          localStorage.setItem('currentEventId', evt.id);
        }
      });
  }, [session?.user?.id]);

  // Load active plan on login; fall back to default plan if none found
  useEffect(() => {
    if (!session?.user?.id) return;
    const userId = session.user.id;
    const today = new Date().toISOString().split('T')[0];

    (async () => {
      const { data: rows, error } = await supabase
        .from('user_plans')
        .select('plans(*)')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error) { console.error('plan load failed:', error.message); return; }

      const validPlan = rows
        ?.map(r => r.plans)
        .find(p => p && (!p.expires_at || p.expires_at >= today));

      if (validPlan) { setActivePlan(validPlan); return; }

      // No valid active plan — assign the default plan
      const { data: defaultPlan, error: dpErr } = await supabase
        .from('plans').select('*').eq('name', 'default').single();

      if (dpErr || !defaultPlan) { console.error('default plan not found:', dpErr?.message); return; }

      // Deactivate any stale active plans
      await supabase.from('user_plans').update({ is_active: false })
        .eq('user_id', userId).eq('is_active', true);

      // Assign (or re-activate) the default plan
      await supabase.from('user_plans')
        .upsert({ user_id: userId, plan_id: defaultPlan.id, is_active: true },
          { onConflict: 'user_id,plan_id' });

      setActivePlan(defaultPlan);
    })();
  }, [session?.user?.id]);

  // Load wedding_url whenever the active event changes
  useEffect(() => {
    if (!currentEventId) return;
    setWeddingUrl(null);
    supabase.from('canvas_state').select('wedding_url').eq('id', currentEventId).maybeSingle()
      .then(({ data, error }) => {
        if (error) { console.error('wedding_url load failed:', error.message); return; }
        if (data?.wedding_url) setWeddingUrl(data.wedding_url);
      });
  }, [currentEventId]);


  const loadVenues = async () => {
    const [{ data: vData, error: vErr }, { data: eData }, { data: csData }] = await Promise.all([
      supabase.from('venues').select('*').order('name', { ascending: true }),
      supabase.from('events').select('venue').not('venue', 'is', null),
      supabase.from('canvas_state').select('venue_name, venue_address').not('venue_name', 'is', null),
    ]);
    if (vErr) { console.error('venues load failed:', vErr.message); return; }
    const existing = new Set((vData ?? []).map(v => v.name));
    const fromEvents = (eData ?? []).map(e => ({ name: e.venue })).filter(v => v.name && !existing.has(v.name));
    const fromCanvas = (csData ?? []).filter(r => r.venue_name && !existing.has(r.venue_name))
      .map(r => ({ name: r.venue_name, address: r.venue_address || null }));
    const extras = [...fromEvents, ...fromCanvas].filter((v, i, arr) => arr.findIndex(x => x.name === v.name) === i);
    if (extras.length) {
      await supabase.from('venues').upsert(extras, { onConflict: 'name' });
      const { data: fresh } = await supabase.from('venues').select('*').order('name', { ascending: true });
      setVenues(fresh ?? []);
    } else {
      setVenues(vData ?? []);
    }
  };

  useEffect(() => { if (session?.user?.id) loadVenues(); }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEventsOpen = () => { setPendingNewEventGuests([]); setShowEventsModal(true); };

  const handleDateClick = () => {
    const d = currentEventDate ?? new Date();
    setCalNavDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setShowCalendar(true);
  };

  const handleCalendarDayClick = async (day) => {
    const y = calNavDate.getFullYear();
    const m = calNavDate.getMonth();
    const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const { error } = await supabase.from('events').update({ date: iso }).eq('id', currentEventId);
    if (error) { console.error('date save failed:', error.message); return; }
    setEvents(prev => prev.map(e => e.id === currentEventId ? { ...e, date: iso } : e));
    setShowCalendar(false);
  };

  const handleEventSwitch = (eventId) => {
    setCurrentEventId(eventId);
    localStorage.setItem('currentEventId', eventId);
    setGroomName(null);
    setBrideName(null);
  };

  const handleEventUpdate = async (eventId, fields) => {
    const { error } = await supabase.from('events').update(fields).eq('id', eventId);
    if (error) { console.error('event update failed:', error.message); return; }
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...fields } : e));
    if (eventId !== currentEventId) {
      setCurrentEventId(eventId);
      localStorage.setItem('currentEventId', eventId);
      setGroomName(null);
      setBrideName(null);
    }
  };

  const handleEventCreate = async (eventData) => {
    if (events.length >= (activePlan?.max_events ?? 1)) return;
    const { data, error } = await supabase.from('events').insert({ ...eventData, user_id: session.user.id }).select().single();
    if (error) { console.error('event create failed:', error.message); return; }
    if (pendingNewEventGuests.length > 0) {
      const rows = pendingNewEventGuests.map(g => ({ ...g, event_id: data.id }));
      await supabase.from('guests').insert(rows);
      setPendingNewEventGuests([]);
    }
    setEvents(prev => [...prev, data].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')));
    setCurrentEventId(data.id);
    localStorage.setItem('currentEventId', data.id);
  };

  const handleNamesClick = () => {
    if (weddingUrl) return;
    setUrlInput('');
    setUrlError('');
    setShowUrlModal(true);
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    setUrlError('');
    const raw = urlInput.trim();
    let parsed;
    try {
      parsed = new URL(raw);
    } catch {
      setUrlError('Please enter a valid URL (e.g. https://example.com)');
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      setUrlError('URL must begin with http:// or https://');
      return;
    }
    setUrlValidating(true);
    try {
      await fetch(raw, { mode: 'no-cors' });
      setWeddingUrl(raw);
      setShowUrlModal(false);
      supabase.from('canvas_state').upsert({ id: currentEventId, wedding_url: raw })
        .then(({ error }) => { if (error) console.error('wedding url save failed:', error.message); });
    } catch {
      setUrlError('Could not reach that page. Please check the URL and try again.');
    } finally {
      setUrlValidating(false);
    }
  };

  const handleAgree = async () => {
    const { error } = await supabase.auth.updateUser({ data: { tos_agreed_at: new Date().toISOString() } });
    if (error) { console.error('agreement save failed:', error.message); return; }
    setNeedsAgreement(false);
  };

  const handleDecline = () => { supabase.auth.signOut(); };

  const openAbout = () => {
    setAboutKey(k => k + 1);
    setAboutDone(false);
    setAboutAtBottom(false);
    setAboutOpen(true);
  };

  useEffect(() => {
    if (!aboutDone) return;
    const el = aboutContainerRef.current;
    if (!el) return;
    const check = () => {
      setAboutAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
    };
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, [aboutDone]);

  const handleAboutIndicatorClick = () => {
    setAboutDone(true);
    const el = aboutContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAboutAtBottom(true);
  };

  const isChinese = name => name && /^[一-鿿㐀-䶿豈-﫿]+$/.test(name.replace(/\s+/g, ''));
  const displayName = name => {
    if (!name || !isChinese(name)) return name;
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? parts.reverse().join('') : name;
  };

  const coupleContent = groomName && brideName
    ? <>{displayName(groomName)} <span className="couple-ampersand">&amp;</span> {displayName(brideName)}</>
    : displayName(groomName || brideName);

  const currentEventDate = (() => {
    const d = events.find(e => e.id === currentEventId)?.date;
    if (!d) return null;
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day);
  })();
  const formatHeaderDate = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  if (session === undefined) return null; // auth state loading
  if (!session) return <div className="App App--login"><Login /></div>;
  if (needsAgreement === null) return null; // agreement check pending
  if (needsAgreement) return (
    <div className="App">
      <AgreementModal onAgree={handleAgree} onDecline={handleDecline} />
    </div>
  );

  return (
    <div className="App">
      <div className="app-header">
        <div className="ganache-logo-group">
          <h1 id="ganache"><a href="https://www.ganache.com" id="ganache-text" style={{ textDecoration: 'none', color: 'inherit' }}>ganache</a></h1>
          <div className="ganache-wedding-subtitle">wedding<sup className="ganache-tm">™</sup></div>
        </div>
        {(groomName || brideName) && (
          <div className="header-couple-block">
            {weddingUrl
              ? <a href={weddingUrl} target="_blank" rel="noreferrer" className="couple-names couple-names--link">{coupleContent}</a>
              : <span className="couple-names couple-names--clickable" onClick={handleNamesClick}>{coupleContent}</span>
            }
            {currentEventDate && (
              <div className="header-event-date" onClick={handleDateClick} style={{ cursor: 'pointer' }}>{formatHeaderDate(currentEventDate)}</div>
            )}
          </div>
        )}
      </div>
      <Taskbar topOffset={titleBottom} onAbout={openAbout} onEvents={handleEventsOpen} onSignOut={() => supabase.auth.signOut()} activePlan={activePlan} />
      {screenTooSmall ? (
        <div className="device-too-small">
          <p>This screen is too small to display the reception planner.</p>
          <p>Please switch to an iPad Pro 11&Prime; or larger, or use a computer.</p>
        </div>
      ) : isPortrait ? (
        <div className="device-too-small">
          <p>The reception planner is only supported in landscape mode.</p>
          <p>Please rotate your device to landscape to continue.</p>
        </div>
      ) : (
        <Reception
          key={currentEventId}
          eventId={currentEventId}
          eventDate={events.find(e => e.id === currentEventId)?.date ?? null}
          eventVenue={events.find(e => e.id === currentEventId)?.venue ?? null}
          groomName={groomName}
          setGroomName={setGroomName}
          brideName={brideName}
          setBrideName={setBrideName}
          pendingGuestRole={pendingGuestRole}
          onPendingRoleDone={() => setPendingGuestRole(null)}
          onPendingGuest={(guest) => setPendingNewEventGuests(prev => [...prev, guest])}
          pendingVenueForEvent={pendingVenueForEvent}
          onVenueForEventSelected={(name) => { setPendingVenueForEvent(false); setEventVenueResult(name ?? null); if (name) loadVenues(); }}
          onCoupleSet={async ({ groom_name, bride_name, eventId: eid }) => {
            const { error } = await supabase.from('events').update({ groom_name, bride_name }).eq('id', eid);
            if (error) { console.error('couple sync failed:', error.message); return; }
            setEvents(prev => prev.map(e => e.id === eid ? { ...e, groom_name, bride_name } : e));
          }}
          onVenueReady={loadVenues}
          onVenueSet={async ({ name, eventId: eid }) => {
            const { error } = await supabase.from('events').update({ venue: name }).eq('id', eid);
            if (error) { console.error('event venue save failed:', error.message); return; }
            setEvents(prev => prev.map(e => e.id === eid ? { ...e, venue: name } : e));
          }}
          onDateSet={async ({ iso, eventId: eid }) => {
            const { error } = await supabase.from('events').update({ date: iso }).eq('id', eid);
            if (error) { console.error('date save failed:', error.message); return; }
            setEvents(prev => prev.map(e => e.id === eid ? { ...e, date: iso } : e));
          }}
        />
      )}

      {showUrlModal && (
        <div className="url-modal-overlay" onClick={() => setShowUrlModal(false)}>
          <div className="url-modal" onClick={e => e.stopPropagation()}>
            <h2>Wedding Website</h2>
            <p>Enter the URL for your wedding website.</p>
            <form onSubmit={handleUrlSubmit}>
              <input
                className="url-modal-input"
                type="text"
                placeholder="https://yourweddingsite.com"
                value={urlInput}
                onChange={e => { setUrlInput(e.target.value); setUrlError(''); }}
                autoFocus
              />
              {urlError && <p className="url-modal-error">{urlError}</p>}
              <div className="url-modal-buttons">
                <button type="button" onClick={() => setShowUrlModal(false)}>Cancel</button>
                <button type="submit" disabled={urlValidating}>
                  {urlValidating ? 'Checking…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEventsModal && (
        <EventsModal
          events={events.slice(0, activePlan?.max_events ?? 1)}
          maxEvents={activePlan?.max_events ?? 1}
          currentEventId={currentEventId}
          groomName={groomName}
          brideName={brideName}
          onSwitch={handleEventSwitch}
          onCreate={handleEventCreate}
          onUpdate={handleEventUpdate}
          onAddGroom={() => setPendingGuestRole({ role: 'groom', forNewEvent: true })}
          onAddBride={() => setPendingGuestRole({ role: 'bride', forNewEvent: true })}
          allVenues={venues}
          onRequestVenue={() => { setEventVenueResult(null); setPendingVenueForEvent(true); }}
          venueResult={eventVenueResult}
          onClose={() => setShowEventsModal(false)}
        />
      )}

      {showCalendar && (
        <div className="url-modal-overlay" onClick={() => setShowCalendar(false)}>
          <div className="calendar-modal" onClick={e => e.stopPropagation()}>
            <div className="cal-nav">
              <button onClick={() => setCalNavDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>‹</button>
              <span>{calNavDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              <button onClick={() => setCalNavDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>›</button>
            </div>
            <div className="cal-grid">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(h => (
                <div key={h} className="cal-day-header">{h}</div>
              ))}
              {(() => {
                const y = calNavDate.getFullYear();
                const m = calNavDate.getMonth();
                const firstDay = new Date(y, m, 1).getDay();
                const daysInMonth = new Date(y, m + 1, 0).getDate();
                const selectedDay = currentEventDate &&
                  currentEventDate.getFullYear() === y &&
                  currentEventDate.getMonth() === m
                  ? currentEventDate.getDate() : null;
                const cells = [];
                for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="cal-day" />);
                for (let d = 1; d <= daysInMonth; d++) {
                  cells.push(
                    <div
                      key={d}
                      className={`cal-day cal-day-active${selectedDay === d ? ' cal-day-selected' : ''}`}
                      onClick={() => handleCalendarDayClick(d)}
                    >{d}</div>
                  );
                }
                const trailing = 42 - firstDay - daysInMonth;
                for (let i = 0; i < trailing; i++) cells.push(<div key={`t${i}`} className="cal-day" />);
                return cells;
              })()}
            </div>
          </div>
        </div>
      )}

      {aboutOpen && (
        <div className="about-overlay" onClick={() => setAboutOpen(false)}>
          <div className="about-modal" onClick={e => e.stopPropagation()}>
            <button className="about-close" onClick={() => setAboutOpen(false)}>×</button>
            <div className="about-brand">
              <span className="about-brand-ganache">ganache</span>
              <span className="about-brand-wedding">wedding<sup className="ganache-tm">™</sup></span>
            </div>
            <div
              ref={aboutContainerRef}
              className={`about-scroll-container${aboutDone ? ' about-scroll-container--done' : ''}`}
            >
              <p
                key={`text-${aboutKey}`}
                className={`about-scroll-text${aboutDone ? ' about-scroll-text--done' : ''}`}
                onAnimationEnd={() => setAboutDone(true)}
              >{ABOUT_TEXT}</p>
            </div>
            {!aboutAtBottom && (
              <button className="about-more-indicator" onClick={handleAboutIndicatorClick}>▼</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
